import React, { useState } from 'react';
import {
  TextField,
  Grid,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  AgronodButton as Button,
  AgronodDialog,
} from '@agronod/mui-components';
import { CreateIssuerRequest } from '@/types';
import IssuerLogoField from '@/components/admin/IssuerLogoField';

interface CreateIssuerModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateIssuerRequest) => Promise<void>;
  isLoading?: boolean;
}

/**
 * Modal for creating a new issuer (certification organization).
 *
 * Validates required fields (code, displayName) and format requirements
 * (email, URLs) before submission. Follows the pattern established by
 * CreateControlTypeModal for consistency.
 */
const CreateIssuerModal: React.FC<CreateIssuerModalProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CreateIssuerRequest>({
    code: '',
    displayName: '',
    description: '',
    website: '',
    logoUrl: '',
    contactEmail: '',
    contactPhone: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Validates form data before submission.
   *
   * Checks:
   * - Required fields: code (1-50 chars), displayName (1-255 chars)
   * - Email format: contactEmail if provided
   * - URL format: website and logoUrl if provided
   *
   * @returns true if all validations pass, false otherwise
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required field validations
    if (!formData.code?.trim()) {
      newErrors['code'] = 'Kod är obligatorisk';
    } else if (formData.code.length > 50) {
      newErrors['code'] = 'Kod får vara max 50 tecken';
    }

    if (!formData.displayName?.trim()) {
      newErrors['displayName'] = 'Visningsnamn är obligatoriskt';
    } else if (formData.displayName.length > 255) {
      newErrors['displayName'] = 'Visningsnamn får vara max 255 tecken';
    }

    // Optional field validations
    if (formData.description && formData.description.length > 2000) {
      newErrors['description'] = 'Beskrivning får vara max 2000 tecken';
    }

    // Email validation
    if (formData.contactEmail?.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contactEmail)) {
        newErrors['contactEmail'] = 'Ogiltig e-postadress';
      }
    }

    // URL validation for website
    if (formData.website?.trim()) {
      const urlRegex = /^https?:\/\/.+\..+/;
      if (!urlRegex.test(formData.website)) {
        newErrors['website'] = 'Ogiltig URL (måste börja med http:// eller https://)';
      }
    }

    // Phone validation (max length)
    if (formData.contactPhone && formData.contactPhone.length > 50) {
      newErrors['contactPhone'] = 'Telefonnummer får vara max 50 tecken';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission.
   * Validates form data, submits to parent, and closes modal on success.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Trim all string fields and remove empty optional fields
      const submitData: CreateIssuerRequest = {
        code: formData.code.trim(),
        displayName: formData.displayName.trim(),
        ...(formData.description?.trim() && { description: formData.description.trim() }),
        ...(formData.website?.trim() && { website: formData.website.trim() }),
        ...(formData.logoUrl?.trim() && { logoUrl: formData.logoUrl.trim() }),
        ...(formData.contactEmail?.trim() && { contactEmail: formData.contactEmail.trim() }),
        ...(formData.contactPhone?.trim() && { contactPhone: formData.contactPhone.trim() }),
      };

      await onSubmit(submitData);
      handleClose();
    } catch (error) {
      console.error('Error creating issuer:', error);
    }
  };

  /**
   * Resets form state and closes the modal.
   */
  const handleClose = () => {
    setFormData({
      code: '',
      displayName: '',
      description: '',
      website: '',
      logoUrl: '',
      contactEmail: '',
      contactPhone: '',
    });
    setErrors({});
    onClose();
  };

  /**
   * Creates a change handler for a specific form field.
   * Clears validation errors when user starts typing.
   */
  const handleChange = (field: keyof CreateIssuerRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <AgronodDialog
      open={open}
      onClose={handleClose}
      title="Skapa Issuer"
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      slotProps={{
        paper: {
          sx: { minHeight: '500px' }
        }
      }}
      actions={
        <>
          <Button
            onClick={handleClose}
            disabled={isLoading}
          >
            Avbryt
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
            form="create-issuer-form"
          >
            {isLoading ? 'Skapar...' : 'Skapa'}
          </Button>
        </>
      }
    >
      <form id="create-issuer-form" onSubmit={handleSubmit}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Kod"
                value={formData.code}
                onChange={handleChange('code')}
                error={!!errors['code']}
                helperText={errors['code'] || 'Unik identifierare (t.ex. KRAV, EU_ORGANIC)'}
                required
                placeholder="t.ex. KRAV"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Visningsnamn"
                value={formData.displayName}
                onChange={handleChange('displayName')}
                error={!!errors['displayName']}
                helperText={errors['displayName'] || 'Namn som visas i användargränssnittet'}
                required
                placeholder="t.ex. KRAV Ekologiskt"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Beskrivning"
                value={formData.description}
                onChange={handleChange('description')}
                error={!!errors['description']}
                helperText={errors['description'] || 'Beskrivning av utfärdaren (valfritt)'}
                multiline
                rows={3}
                placeholder="Beskriv utfärdarens roll och certifieringar..."
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Webbplats"
                value={formData.website}
                onChange={handleChange('website')}
                error={!!errors['website']}
                helperText={errors['website'] || 'URL till utfärdarens webbplats (valfritt)'}
                placeholder="https://www.example.com"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <IssuerLogoField
                value={formData.logoUrl ?? null}
                onChange={(v) => setFormData((prev) => ({ ...prev, logoUrl: v ?? '' }))}
                disabled={isLoading}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Kontakt E-post"
                value={formData.contactEmail}
                onChange={handleChange('contactEmail')}
                error={!!errors['contactEmail']}
                helperText={errors['contactEmail'] || 'E-postadress för kontakt (valfritt)'}
                placeholder="info@example.com"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Kontakt Telefon"
                value={formData.contactPhone}
                onChange={handleChange('contactPhone')}
                error={!!errors['contactPhone']}
                helperText={errors['contactPhone'] || 'Telefonnummer för kontakt (valfritt)'}
                placeholder="+46 8 123 456 78"
              />
            </Grid>
          </Grid>
        </Box>
      </form>
    </AgronodDialog>
  );
};

export default CreateIssuerModal;
